import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const TYPE_LABELS = {
  matrix: 'Matris diaqramı',
  table: 'Nəticə cədvəli',
  special: 'HRT / Manipura',
  text: 'Sərbəst mətn',
  grid: 'Hüceyrə şəbəkəsi',
  energy: 'Karmik Enerji',
};

const TYPE_COLORS = {
  matrix: '#a855f7',
  table: '#0ea5e9',
  special: '#eab308',
  text: '#22c55e',
  grid: '#06b6d4',
  energy: '#f59e0b',
};

function SortableSection({ section, onUpdate, onDelete, expanded, onToggle }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="sortable-item" {...attributes}>
      <div className="sortable-grip" {...listeners}>
        <GripVertical size={18} />
      </div>
      <div className="sortable-dot" style={{ background: TYPE_COLORS[section.type] || '#888' }} />
      <div className="flex-1 flex flex-col min-w-0">
        <span className="sortable-label truncate">
          {section.title || TYPE_LABELS[section.type] || section.type}
        </span>
        <span className="text-xs text-white/30 mt-0.5">
          {TYPE_LABELS[section.type] || section.type} · id: {section.id}
        </span>
        {expanded && (
          <div className="flex flex-col gap-2 mt-3">
            <input
              className="karma-input w-full text-left"
              placeholder="Bölmə başlığı (boş = göstərilmir)"
              value={section.title || ''}
              onChange={(e) => onUpdate({ ...section, title: e.target.value })}
              data-testid={`section-title-${section.id}`}
            />
            {section.type === 'text' && (
              <textarea
                className="karma-input w-full text-left"
                style={{ minHeight: 80, fontFamily: 'inherit' }}
                placeholder="Mətn məzmunu..."
                value={section.content || ''}
                onChange={(e) => onUpdate({ ...section, content: e.target.value })}
                data-testid={`section-content-${section.id}`}
              />
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="admin-btn-secondary"
        title="Detallar"
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="admin-btn-secondary"
        title="Sil"
        data-testid={`section-delete-${section.id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

const SectionsEditor = ({ sections, onChange }) => {
  const items = sections || [];
  const [expanded, setExpanded] = useState({});
  const [newType, setNewType] = useState('text');
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(s => s.id === active.id);
      const newIndex = items.findIndex(s => s.id === over.id);
      onChange(arrayMove(items, oldIndex, newIndex));
    }
  };

  const addSection = () => {
    let baseId = newType === 'matrix' || newType === 'table' || newType === 'special'
      ? newType
      : `${newType}_${Date.now().toString(36)}`;
    let id = baseId;
    let n = 2;
    while (items.some(s => s.id === id)) {
      id = `${baseId}_${n++}`;
    }
    const next = [...items, { id, type: newType, title: '', content: '' }];
    onChange(next);
    setExpanded({ ...expanded, [id]: true });
  };

  const updateAt = (idx, updated) => {
    const copy = [...items];
    copy[idx] = updated;
    onChange(copy);
  };

  const confirmDelete = (idx) => {
    setPendingDelete(idx);
  };

  const performDelete = () => {
    if (pendingDelete === null) return;
    const copy = items.filter((_, i) => i !== pendingDelete);
    onChange(copy);
    setPendingDelete(null);
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Bölmələr</h3>
      <p className="admin-section-desc">
        Sürükləyib sıranı dəyişin, başlıq əlavə edin və ya yeni bölmə yaradın.
      </p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select
          className="karma-input"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          style={{ maxWidth: 200 }}
          data-testid="section-new-type"
        >
          <option value="matrix">Matris diaqramı</option>
          <option value="table">Nəticə cədvəli</option>
          <option value="special">HRT / Manipura</option>
          <option value="text">Sərbəst mətn</option>
          <option value="grid">Hüceyrə şəbəkəsi</option>
          <option value="energy">Karmik Enerji</option>
        </select>
        <button
          type="button"
          onClick={addSection}
          className="admin-btn-primary"
          data-testid="section-add-btn"
        >
          <Plus size={14} /> Yeni bölmə
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="sortable-list">
            {items.map((sec, idx) => (
              <SortableSection
                key={sec.id}
                section={sec}
                expanded={!!expanded[sec.id]}
                onToggle={() => setExpanded({ ...expanded, [sec.id]: !expanded[sec.id] })}
                onUpdate={(u) => updateAt(idx, u)}
                onDelete={() => confirmDelete(idx)}
              />
            ))}
            {items.length === 0 && (
              <p className="text-white/40 text-sm">Heç bir bölmə yoxdur. Yuxarıdan əlavə edin.</p>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Inline confirmation banner */}
      {pendingDelete !== null && items[pendingDelete] && (
        <div
          className="mt-4 p-3 rounded border border-red-500/40 bg-red-500/10 flex items-center justify-between gap-3 flex-wrap"
          data-testid="section-delete-confirm-banner"
        >
          <span className="text-sm text-red-200">
            "<strong>{items[pendingDelete].title || items[pendingDelete].id}</strong>" bölməsini silmək istəyirsiniz?
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setPendingDelete(null)}
              data-testid="section-delete-cancel"
            >
              Ləğv et
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              style={{ background: 'rgba(239,68,68,0.85)', borderColor: '#ef4444' }}
              onClick={performDelete}
              data-testid="section-delete-confirm"
            >
              Sil
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionsEditor;
